import { useEffect, useState } from "react";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import { useLocation, useNavigate } from "react-router-dom";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import useHost from "containers/host/useHooks";
import useStore from "hooks/useStore";
import moment from "moment";
import { Rating } from "@smastrom/react-rating";
import { useSelector } from "react-redux";
import "@smastrom/react-rating/style.css";
import ExperienceDetailHeader from "components/experience-detail-header";
import WorkshopSessionPopup from "components/popups/workshop-session";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";

interface Review {
  id: number;
  userId: number;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    profilePic: {
      fileDownloadUri: string;
      id: number;
    };
  };
  comment: string;
  createdAt: Date;
  rating: number;
}

interface Cups {
  id: number;
  image: {
    fileDownloadUri: string;
    id: number;
  };
  price: string;
  name: string;
  quantity: number;
}

const ReviewsAndCup: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"Reviews" | "Cups">("Reviews");
  const [workshopSessionModal, setWorkshopSessionModal] = useState(false);
  const navigate = useNavigate();
  const { isProfileCompleted } = useStore();
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },
    { title: "Rating view", path: siteRoutes.reviewsCups },
  ];
  const location = useLocation();
  const { eventData } = location.state as { eventData: any };
  if (!eventData) {
    navigate(siteRoutes.experiences);
  }

  const [reviewsData, setReviewsData] = useState<Review[]>([]);
  const [cupsData, setCupsData] = useState<Cups[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { getReviewsByEventId, getCupsByEventId } = useHost();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.home, { replace: true });
    }
  }, [isProfileCompleted, navigate]);
  useEffect(() => {
    getReviewsByEventId(eventData.id, setReviewsData);
    getCupsByEventId(eventData.id, setCupsData);
  }, []);

  // Mark initial load as complete when loading finishes
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const lastSession =
    eventData.workshopSessions[eventData.workshopSessions.length - 1];
  const lastSessionDateTime = new Date(
    `${lastSession.startDateWithMonthAndYear} ${lastSession.endTime}`
  );
  const hasStarted = new Date() >= lastSessionDateTime;

  const visibleSessions = eventData.workshopSessions.slice(0, 3);
  const hasMoreSessions = eventData.workshopSessions.length > 3;

  return (
    <div>
      <ExperienceDetailHeader
        event={eventData}
        breadcrumb={<Breadcrumb links={breadcrumbLinks} />}
        // onHostClick={goToHostDetail}
        onSeeMoreSessions={() => setWorkshopSessionModal(true)}
        visibleSessions={visibleSessions}
        hasMoreSessions={hasMoreSessions}
        gradientClass="bg-gradient"
      />
      <div className="container py-6">
        <div className="">
          <h2 className="text-2xl font-bold my-4">
            Reviews & Cup of Gratitude
          </h2>
          <div className="flex justify-start my-10">
            {/* Tabs */}
            <button
              className={`px-4 py-2 text-sm font-semibold ${
                activeTab === "Reviews"
                  ? "text-lightBlueColor border-b-2 border-lightBlueColor"
                  : "text-grayText"
              }`}
              onClick={() => setActiveTab("Reviews")}
            >
              Reviews
            </button>
            <button
              className={`px-4 py-2 text-sm font-semibold ${
                activeTab === "Cups"
                  ? "text-lightBlueColor border-b-2 border-lightBlueColor"
                  : "text-grayText"
              }`}
              onClick={() => setActiveTab("Cups")}
            >
              Cup of Gratitude
            </button>
          </div>

          <div className="my-4">
            {activeTab === "Reviews" ? (
              <div className="border-[0.3px] border-labelTextColor  p-6  rounded-lg shadow-sm flex flex-col gap-10">
                {reviewsData.length > 0
                  ? reviewsData.map((review) => (
                      <div
                        key={review.id}
                        className="flex md:flex-nowrap flex-wrap items-start justify-between "
                      >
                        {/* Left Column - User Info and Review */}
                        <div className="flex flex-col gap-3 w-full">
                          {/* User Info */}
                          <div className="flex items-center gap-2">
                            <img
                              src={concatPathWithBackendUrl(
                                review?.user?.profilePic?.fileDownloadUri
                              )}
                              alt="Avatar"
                              className="w-10 h-10 rounded-full"
                            />
                            <div>
                              <h4 className="font-semibold text-black text-lg">
                                {review.user.firstName} {review.user.lastName}
                              </h4>
                              <p className="text-sm text-labelTextColor">
                                {moment(review.createdAt).fromNow()}
                              </p>
                            </div>
                          </div>

                          {/* Review Text */}
                          <div>
                            <p
                              className={` ${
                                review.comment
                                  ? "text-blackie"
                                  : "text-labelTextColor"
                              }`}
                            >
                              {review.comment || "No Comment Provided"}
                            </p>
                          </div>
                        </div>

                        {/* Right Column - Rating and Cup Icon */}
                        <div className="flex flex-col gap-2 md:gap-3 md:items-end md:px-6 mt-3">
                          {/* Rating */}
                          <div className="flex items-center space-x-2">
                            <div className="flex items-center w-auto">
                              <Rating
                                value={review.rating}
                                readOnly
                                radius="medium"
                                style={{
                                  width: "100%",
                                  height: "25px",
                                }}
                                spaceBetween="none"
                              />
                            </div>
                            <span className="ml-2 text-labelTextColor">
                              ({review.rating})
                            </span>
                          </div>

                          {/* Cup Rating */}
                          <div></div>
                        </div>
                      </div>
                    ))
                  : null}

                <CustomMessageDisplay
                  show={!isLoading && !reviewsData.length && !isInitialLoad}
                  title="No Reviews Available"
                  slogan="No reviews have been submitted yet. Reviews will appear here once attendees share their feedback."
                  image={noFoundGif}
                  imageAlt="No reviews found"
                  className="py-12"
                  imageClassName="w-24 h-24"
                  titleClassName="text-2xl font-bold text-blackie"
                  sloganClassName="text-gray-500 text-base"
                />
              </div>
            ) : (
              <div className="flex flex-wrap gap-4">
                {cupsData.length > 0
                  ? cupsData.map((cup) => (
                      <div
                        key={cup.id}
                        className="relative text-center w-[185px]"
                      >
                        <img
                          src={concatPathWithBackendUrl(
                            cup.image?.fileDownloadUri
                          )}
                          alt={cup.name}
                          className=""
                        />

                        <h3 className="text-blackie text-4xl font-bold my-2">
                          {cup.name}
                        </h3>
                        <div className="mx-auto w-fit mt-2 flex flex-row gap-2">
                          <p className="text-xs bg-yellowBg text-yellowText px-3 py-1 rounded-full">
                            ${cup.price}
                          </p>
                          <p className="text-xs bg-yellowBg text-yellowText px-3 py-1 rounded-full">
                            {cup.quantity} times
                          </p>
                        </div>
                      </div>
                    ))
                  : null}

                <CustomMessageDisplay
                  show={!isLoading && !cupsData.length && !isInitialLoad}
                  title="No Cups of Gratitude Available"
                  slogan="No cups of gratitude have been received yet. Cups will appear here once attendees show their appreciation."
                  image={noFoundGif}
                  imageAlt="No cups found"
                  className="py-12"
                  imageClassName="w-24 h-24"
                  titleClassName="text-2xl font-bold text-blackie"
                  sloganClassName="text-gray-500 text-base"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {workshopSessionModal && (
        <WorkshopSessionPopup
          isOpen={workshopSessionModal}
          onClose={() => setWorkshopSessionModal(false)}
          sessions={eventData.workshopSessions}
        />
      )}
    </div>
  );
};

export default ReviewsAndCup;
